import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  FileText,
  Calendar,
  Building2,
  User,
  CheckCircle,
  XCircle,
  Loader2,
  Info,
} from "lucide-react";
import apiClient from "../../services/api";
import { toast } from "react-hot-toast";

interface InvoiceGenerationParams {
  year?: number;
  month?: number;
  frequency?: "weekly" | "monthly" | "quarterly" | "annual";
  branchId?: string;
  studentId?: string;
  consolidate?: boolean;
}

interface GenerationResult {
  period: {
    year: number;
    month: number;
    monthName: string;
    frequency: string;
  };
  statistics: {
    invoicesCreated: number;
    invoicesSkipped: number;
    notificationsPending: number;
    totalProcessed: number;
  };
  configuration: {
    consolidation: boolean;
    branchFilter: string;
    studentFilter: string;
  };
  initiatedBy: {
    userId: string;
    name: string;
    role: string;
  };
  timestamp: string;
}

const ManualInvoiceGeneration: React.FC = () => {
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [frequency, setFrequency] = useState<string>("monthly");
  const [consolidate, setConsolidate] = useState(true);
  const [result, setResult] = useState<GenerationResult | null>(null);

  const generateMutation = useMutation({
    mutationFn: async (params: InvoiceGenerationParams) => {
      const response = await apiClient.post(
        "/fees/admin/generate-invoices",
        params,
      );
      return response.data;
    },
    onSuccess: (data) => {
      setResult(data.data);
      toast.success(data.message || "Invoices generated successfully!");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message ||
          "Failed to generate invoices. Please try again.",
      );
      console.error("Invoice generation error:", error);
    },
  });

  const handleGenerate = () => {
    generateMutation.mutate({
      year,
      month,
      frequency: frequency as any,
      consolidate,
    });
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-3 shadow-lg">
          <FileText className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">
            Manual Invoice Generation
          </h2>
          <p className="text-sm text-slate-600 font-medium mt-1">
            Generate invoices for any period as a failsafe
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">When to use this feature:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Automated cron jobs failed to generate invoices</li>
              <li>Need to regenerate invoices for a specific period</li>
              <li>Testing invoice generation for past or future months</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Generation Form */}
        <div className="space-y-4">
          {/* Year Selection */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              Year
            </label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              min="2020"
              max="2100"
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
            />
          </div>

          {/* Month Selection */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              Month
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
            >
              {monthNames.map((name, index) => (
                <option key={index + 1} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* Frequency Selection */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Billing Frequency
            </label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
            >
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>

          {/* Consolidation Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div>
              <label className="text-sm font-bold text-slate-900 block">
                Consolidate Invoices
              </label>
              <p className="text-xs text-slate-600 mt-1">
                Combine multiple courses into one invoice per student
              </p>
            </div>
            <button
              type="button"
              onClick={() => setConsolidate(!consolidate)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                consolidate ? "bg-blue-600" : "bg-slate-300"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
                  consolidate ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={generateMutation.isPending}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileText className="h-5 w-5" />
                Generate Invoices
              </>
            )}
          </button>
        </div>

        {/* Results Display */}
        <div>
          {generateMutation.isPending && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-3" />
              <p className="text-sm font-semibold text-blue-800">
                Generating invoices...
              </p>
              <p className="text-xs text-blue-600 mt-1">
                This may take a few moments
              </p>
            </div>
          )}

          {generateMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <XCircle className="h-8 w-8 text-red-600" />
                <h3 className="font-bold text-red-800">Generation Failed</h3>
              </div>
              <p className="text-sm text-red-700">
                {(generateMutation.error as any)?.response?.data?.message ||
                  "An error occurred while generating invoices."}
              </p>
            </div>
          )}

          {result && !generateMutation.isPending && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <h3 className="font-bold text-green-800">
                  Generation Complete!
                </h3>
              </div>

              <div className="space-y-3">
                {/* Period Info */}
                <div className="bg-white rounded-lg p-3 border border-green-200">
                  <p className="text-xs font-semibold text-slate-600 mb-1">
                    Period
                  </p>
                  <p className="text-sm font-bold text-slate-900">
                    {result.period.monthName}
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    Frequency: {result.period.frequency}
                  </p>
                </div>

                {/* Statistics */}
                <div className="bg-white rounded-lg p-3 border border-green-200">
                  <p className="text-xs font-semibold text-slate-600 mb-2">
                    Statistics
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-600">Created</span>
                      <span className="text-sm font-bold text-green-600">
                        {result.statistics.invoicesCreated}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-600">Skipped</span>
                      <span className="text-sm font-bold text-amber-600">
                        {result.statistics.invoicesSkipped}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-600">
                        Notifications
                      </span>
                      <span className="text-sm font-bold text-blue-600">
                        {result.statistics.notificationsPending}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                      <span className="text-xs font-semibold text-slate-700">
                        Total Processed
                      </span>
                      <span className="text-sm font-bold text-slate-900">
                        {result.statistics.totalProcessed}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Configuration */}
                <div className="bg-white rounded-lg p-3 border border-green-200">
                  <p className="text-xs font-semibold text-slate-600 mb-2">
                    Configuration
                  </p>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Consolidation</span>
                      <span className="font-semibold text-slate-900">
                        {result.configuration.consolidation ? "Yes" : "No"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Branch Filter</span>
                      <span className="font-semibold text-slate-900">
                        {result.configuration.branchFilter}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Initiated By */}
                <div className="bg-white rounded-lg p-3 border border-green-200">
                  <p className="text-xs font-semibold text-slate-600 mb-1">
                    Initiated By
                  </p>
                  <p className="text-sm font-bold text-slate-900">
                    {result.initiatedBy.name}
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    {new Date(result.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!result &&
            !generateMutation.isPending &&
            !generateMutation.isError && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
                <FileText className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                <p className="text-sm font-semibold text-slate-600">
                  No results yet
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Configure the settings and click "Generate Invoices"
                </p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default ManualInvoiceGeneration;
